import React from "react";
import { Helmet } from "react-helmet-async";

// Global base schema (applies to ALL pages)
const globalSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Groomie Roomie",
  "url": "https://groomieroomie.com",
  "logo": "https://groomieroomie.com/logo.png",
  "email": "groomieroomie@gmail.com",
  "telephone": "+17209738622",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2145 S Newport St",
    "addressLocality": "Denver",
    "addressRegion": "CO",
    "postalCode": "80224",
    "addressCountry": "US"
  },
  "sameAs": [
    "https://www.instagram.com/groomie_roomie_denver_",
    "https://www.tiktok.com/@groomie_roomie_denver"
  ]
};

// Homepage schema
const homeSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Groomie Roomie - Mobile Dog & Cat Grooming",
  "image": "https://groomieroomie.com/mobile-dog-grooming-van.jpg",
  "description": "Mobile dog and cat grooming in Denver. Fully equipped van serving Cherry Creek, Highlands Ranch, Lakewood, Aurora & 20+ surrounding areas.",
  "url": "https://groomieroomie.com",
  "priceRange": "$$",
  "telephone": "+17209738622",
  "openingHours": "Mo-Su 08:00-20:00",
  "address": globalSchema.address,
  "areaServed": [
    "Arvada", "Aurora", "Capitol Hill", "Centennial", "Central Park", "Cherry Creek",
    "Cherry Hills", "Columbine", "Commerce City", "Denver", "DTC", "Edgewater",
    "Englewood", "Glendale", "Golden", "Greenwood Village", "Highlands",
    "Highlands Ranch", "Ken Caryl", "Lakewood", "Littleton", "RiNo", "Wash Park",
    "Wheat Ridge"
  ],
  "serviceType": "Mobile Dog Grooming, Mobile Cat Grooming"
};

// Services page schema
const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Mobile Grooming Services in Denver",
  "url": "https://groomieroomie.com/services",
  "provider": { "@type": "LocalBusiness", "name": "Groomie Roomie" },
  "serviceType": ["Dog grooming", "Cat grooming", "Mobile grooming", "Nail trimming", "Deshedding", "Spa treatments"],
  "areaServed": "Denver, Colorado"
};

// Gallery schema
const gallerySchema = {
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  "name": "Before & After Grooming Gallery",
  "url": "https://groomieroomie.com/gallery",
  "description": "Before and after mobile grooming transformations across Denver."
};

// About page schema
const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Groomie Roomie",
  "url": "https://groomieroomie.com/about",
  "description": "Certified groomers, mission, story, and grooming approach.",
  "publisher": { "@type": "Organization", "name": "Groomie Roomie" }
};

// Contact page schema
const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Groomie Roomie",
  "url": "https://groomieroomie.com/contact",
  "description": "Contact our mobile grooming team for appointments or questions."
};

// FAQ page schema
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much does mobile dog grooming cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most mobile dog grooming appointments in Denver range from $95 to $150 depending on size and coat."
      }
    },
    {
      "@type": "Question",
      "name": "Do you groom cats?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes — we offer specialized cat grooming including lion cuts, mat removal, and sanitary trims."
      }
    },
    {
      "@type": "Question",
      "name": "What areas do you serve?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We serve Cherry Creek, Denver, Highlands Ranch, Lakewood, Aurora, and 20+ surrounding areas."
      }
    },
    {
      "@type": "Question",
      "name": "Is mobile grooming good for anxious pets?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes — mobile grooming provides a quiet, 1-on-1 experience without cages or noisy salons."
      }
    }
  ]
};

// Loyalty page schema
const loyaltySchema = {
  "@context": "https://schema.org",
  "@type": "Offer",
  "name": "Pawsome Rewards Loyalty Program",
  "description": "Earn points every time your pet gets groomed. Redeem for discounts, spa upgrades, nail care, and seasonal perks.",
  "url": "https://groomieroomie.com/loyalty"
};

// Blog homepage schema
const blogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Groomie Roomie Blog",
  "url": "https://groomieroomie.com/blog",
  "description": "Pet care tips and grooming advice for Denver pet owners."
};

// Blog post schema generator
const blogPostSchema = (post: any) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": post?.title || "",
  "description": post?.excerpt || "",
  "image": post?.image || "",
  "author": { "@type": "Person", "name": "Anastassia" },
  "publisher": { "@type": "Organization", "name": "Groomie Roomie" },
  "datePublished": post?.date || "",
  "url": post?.url || ""
});

// Location schema generator
const locationSchema = (locationName: string, slug: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": `Mobile Pet Grooming in ${locationName}`,
  "url": `https://groomieroomie.com/locations/${slug}`,
  "serviceType": "Mobile pet grooming",
  "provider": { "@type": "LocalBusiness", "name": "Groomie Roomie" },
  "areaServed": `${locationName}, Denver CO`,
  "hasMap": `https://maps.google.com/?q=${encodeURIComponent(locationName + " Denver")}`
});

// Master component: Detects route and injects correct schema
const SchemaMarkup = ({ blogPost }: any) => {
  const path = window.location.pathname;
  let pageSchema = null;

  if (path === "/") pageSchema = homeSchema;
  else if (path.startsWith("/services")) pageSchema = servicesSchema;
  else if (path.startsWith("/gallery")) pageSchema = gallerySchema;
  else if (path.startsWith("/about")) pageSchema = aboutSchema;
  else if (path.startsWith("/contact")) pageSchema = contactSchema;
  else if (path.startsWith("/faq")) pageSchema = faqSchema;
  else if (path.startsWith("/loyalty")) pageSchema = loyaltySchema;
  else if (path.startsWith("/blog/") && blogPost) pageSchema = blogPostSchema(blogPost);
  else if (path.startsWith("/blog")) pageSchema = blogSchema;
  else if (path.startsWith("/locations/")) {
    const slug = path.split("/")[2];
    const name = slug.replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
    pageSchema = locationSchema(name, slug);
  }

  const mergedSchema = pageSchema ? [globalSchema, pageSchema] : [globalSchema];

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(mergedSchema)}
      </script>
    </Helmet>
  );
};

export default SchemaMarkup;
